/**
 * Pure helpers for user avatars.
 *
 * Kept free of React / store dependencies so they can be unit-tested
 * in isolation and reused across SPA + display contexts.
 */

import type { RecordModel } from "pocketbase";
import { pb } from "./pb";

export interface UserAvatarRef {
  id: string;
  /** Raw filename stored in PocketBase `users.avatar`. */
  avatar?: string | null;
}

/**
 * Compute up to `max` uppercase initials from a display name.
 * Returns "?" when the name is empty or whitespace.
 */
export function buildInitials(name: string | null | undefined, max = 2): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "?";
  const letters = trimmed
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .toUpperCase();
  return letters.slice(0, max) || "?";
}

/**
 * Build the absolute file URL for a user avatar via the PocketBase SDK.
 * Returns `null` when the user has no avatar.
 *
 * Uses `pb.files.getURL` with a synthetic record stub so callers don't
 * have to pass the full `RecordModel` (which is rarely available
 * outside auth flows).
 */
export function buildUserFileAvatarUrl(user: UserAvatarRef): string | null {
  const filename = (user.avatar ?? "").trim();
  if (!filename) return null;
  const stub = {
    id: user.id,
    collectionName: "users",
  } as unknown as RecordModel;
  const base = pb.files.getURL(stub, filename);
  return base || null;
}

/**
 * Append a cache-busting query param to an avatar URL.
 * No-op when `url` is null/empty or `version` is falsy.
 */
export function withAvatarVersion(url: string | null | undefined, version?: number | string | null): string | null {
  if (!url) return null;
  if (!version) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${encodeURIComponent(String(version))}`;
}
